<template>
  <div class="state-seal-row">
    <!-- Botão quando se pode trocar, e texto quando não: o selo É a troca de situação, e não um
         enfeite ao lado de um botão que diria o mesmo. -->
    <component :is="changeable ? 'button' : 'span'" :type="changeable ? 'button' : undefined"
               class="state-seal" :class="[surface, ink, {'state-seal--action': changeable}]"
               :title="changeable ? changeLabel : undefined" :aria-label="changeable ? changeLabel : undefined"
               @click="changeable && $emit('change')">
      <v-icon size="small" :color="dot">{{ VALUE_DOT }}</v-icon>
      <span class="state-seal-name">{{ state.name }}</span>
      <v-icon v-if="changeable" size="small" class="state-seal-chevron">mdi-chevron-down</v-icon>
    </component>
    <span v-if="since" class="text-caption text-medium-emphasis" :title="sinceExact">{{ sinceText }}</span>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {ColoredOption, VALUE_DOT} from '@/types/ColoredOption'
import {NO_VALUE_COLOR, accentColor, surfaceClass, titleClass} from '@/utils/colors'
import {exactTime, relativeTime} from '@/utils/relativeTime'

/**
 * A situação do item no alto da ficha (pedido do usuário: marcante, no padrão de ícone da casa) —
 * a bolinha da situação e o nome numa pílula tingida da cor dela, e ao lado há quanto tempo o item
 * está nela.
 *
 * Era um campo a mais no fim da grade, mais o botão "Mudar situação" no cabeçalho: a mesma coisa
 * dita duas vezes, e nenhuma das duas à vista. Agora a pílula é a situação e a troca.
 *
 * Os tons são os da superfície da casa (`surfaceClass`, o `lighten-5` da faixa do Kanban) e a tinta
 * `darken-3` — o amarelo e o lima somem na cor-base sobre fundo claro. A bolinha no tom do acento,
 * que se destaca do fundo.
 */
@Component({emits: ['change'], name: 'ontos-state-seal'})
class StateSeal extends Vue {
  @Prop({type: Object as () => ColoredOption, required: true}) readonly state!: ColoredOption
  /** Desde quando o item está nesta situação (`_stateChangedAt`). Item antigo pode não ter. */
  @Prop({type: String, default: ''}) readonly since!: string
  /** Quem olha pode trocar a situação: a pílula vira o botão. */
  @Prop({type: Boolean, default: false}) readonly changeable!: boolean

  //Exposto ao template: constante de módulo não é enxergada de lá
  VALUE_DOT = VALUE_DOT

  /** O tom mais pálido, o da faixa do Kanban (pedido do usuário: o `lighten-4` pesava no selo). */
  get surface(): string {
    return surfaceClass(this.state.color || NO_VALUE_COLOR)
  }

  get ink(): string {
    return titleClass(this.state.color)
  }

  get dot(): string | undefined {
    return accentColor(this.state.color) || undefined
  }

  get changeLabel(): string {
    return this.$tt('value-change', {property: this.$tt('state')}) as string
  }

  get sinceText(): string {
    return relativeTime(this.since)
  }

  get sinceExact(): string {
    return this.$tt('state-since', {date: exactTime(this.since)}) as string
  }
}

export default toNative(StateSeal)
</script>

<style scoped>
.state-seal-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}

/* Pílula, e não o retângulo do resto da tela, de propósito: é o único selo da ficha, e a forma
   diferente é o que o faz ser lido primeiro. */
.state-seal {
  align-items: center;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  font: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  gap: 6px;
  line-height: 1;
  padding: 6px 14px 6px 10px;
}

.state-seal--action {
  cursor: pointer;
  padding-right: 8px;
  transition: filter 0.15s ease;
}

.state-seal--action:hover {
  filter: brightness(0.96);
}

.state-seal--action:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.state-seal-chevron {
  opacity: 0.7;
}
</style>
